import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, PenLine, RefreshCw, XCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { WritingFeedback } from "@engo/shared";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import { Textarea } from "../../components/ui/textarea.js";
import { ApiError, api } from "../../lib/api.js";

const RATING_STYLES: Record<WritingFeedback["rating"], string> = {
  excellent: "bg-emerald-500/15 text-emerald-300",
  good: "bg-sky-500/15 text-sky-300",
  needs_work: "bg-amber-500/15 text-amber-300",
};

export function WritingPracticePage() {
  const { t } = useTranslation();
  const [text, setText] = useState("");

  const prompt = useMutation({ mutationFn: () => api.generateWritingPrompt() });
  const feedback = useMutation({
    mutationFn: (input: { prompt: string; words: string[]; text: string }) =>
      api.gradeWriting(input),
  });

  function newPrompt() {
    setText("");
    feedback.reset();
    prompt.mutate();
  }

  const task = prompt.data;
  const result = feedback.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
            {t("writing.title")}
          </h1>
          <p className="mt-1 text-[var(--text-secondary)]">{t("writing.subtitle")}</p>
        </div>
        <Button variant="primary" onClick={newPrompt} disabled={prompt.isPending}>
          <RefreshCw className={`h-4 w-4 ${prompt.isPending ? "animate-spin" : ""}`} aria-hidden />
          {prompt.isPending
            ? t("writing.loadingPrompt")
            : task
              ? t("writing.newPrompt")
              : t("writing.getPrompt")}
        </Button>
      </div>

      {prompt.isError ? (
        <Card className="text-rose-300">
          {prompt.error instanceof ApiError ? prompt.error.message : t("writing.error")}
        </Card>
      ) : null}

      {!task && !prompt.isPending && !prompt.isError ? (
        <Card className="flex flex-col items-center gap-3 py-12 text-center">
          <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--primary)]/10 text-[var(--accent)]">
            <PenLine className="h-6 w-6" aria-hidden />
          </span>
          <p className="max-w-md text-sm text-[var(--text-secondary)]">{t("writing.emptyHint")}</p>
        </Card>
      ) : null}

      {prompt.isPending ? <Card>{t("writing.loadingPrompt")}</Card> : null}

      {task ? (
        <>
          <Card className="space-y-3">
            <p className="text-sm font-medium text-[var(--text-primary)]">{t("writing.taskLabel")}</p>
            <p className="text-base text-[var(--text-secondary)]">{task.prompt}</p>
            <p className="text-sm text-[var(--text-muted)]">{task.scenario_vi}</p>
            <div className="flex flex-wrap gap-1.5">
              {task.words.map((word) => (
                <span
                  key={word.word}
                  title={word.vietnamese ?? word.meaning}
                  className="rounded-full bg-[var(--primary)]/10 px-2.5 py-1 text-xs font-medium text-[var(--accent)]"
                >
                  {word.word}
                </span>
              ))}
            </div>
          </Card>

          <Card className="space-y-3">
            <p className="text-sm font-medium text-[var(--text-primary)]">{t("writing.yourAnswer")}</p>
            <Textarea
              rows={5}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("writing.answerPlaceholder")}
            />
            <div className="flex justify-end">
              <Button
                variant="primary"
                disabled={feedback.isPending || text.trim().length === 0}
                onClick={() =>
                  feedback.mutate({
                    prompt: task.prompt,
                    words: task.words.map((w) => w.word),
                    text: text.trim(),
                  })
                }
              >
                {feedback.isPending ? t("writing.grading") : t("writing.submit")}
              </Button>
            </div>
            {feedback.isError ? (
              <p className="text-sm text-rose-300">
                {feedback.error instanceof ApiError ? feedback.error.message : t("writing.error")}
              </p>
            ) : null}
          </Card>
        </>
      ) : null}

      {result ? (
        <Card className="space-y-4">
          <div className="flex items-center gap-3">
            <p className="text-sm font-medium text-[var(--text-primary)]">{t("writing.feedbackTitle")}</p>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-semibold ${RATING_STYLES[result.rating]}`}
            >
              {t(`writing.rating_${result.rating}`)}
            </span>
          </div>
          <p className="text-sm text-[var(--text-secondary)]">{result.summary_vi}</p>

          {result.corrections.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
                {t("writing.correctionsLabel")}
              </p>
              {result.corrections.map((correction, idx) => (
                <div key={idx} className="rounded-lg border border-[var(--glass-border)] p-3 text-sm">
                  <p className="text-rose-300 line-through">{correction.quote}</p>
                  <p className="mt-1 font-medium text-emerald-300">{correction.suggestion}</p>
                  <p className="mt-1 text-xs text-[var(--text-muted)]">{correction.issue_vi}</p>
                </div>
              ))}
            </div>
          ) : null}

          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
              {t("writing.wordUsageLabel")}
            </p>
            <ul className="space-y-1.5">
              {result.word_usage.map((usage) => (
                <li key={usage.word} className="flex items-start gap-2 text-sm">
                  {usage.used && usage.natural ? (
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-400" aria-hidden />
                  ) : (
                    <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-400" aria-hidden />
                  )}
                  <span className="text-[var(--text-secondary)]">
                    <span className="font-medium text-[var(--text-primary)]">{usage.word}</span>
                    {" — "}
                    {usage.comment_vi}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-[var(--text-muted)]">
              {t("writing.improvedLabel")}
            </p>
            <p className="whitespace-pre-wrap rounded-lg border border-[var(--glass-border)] bg-white/[0.03] p-3 text-sm leading-relaxed text-[var(--text-secondary)]">
              {result.improved_version}
            </p>
          </div>
        </Card>
      ) : null}
    </div>
  );
}
